import type { OverlayRegistrationProps } from '@/app/components/overlays/core/types';
import { getOverlayRegistryKey } from '@/app/components/overlays/utils/getOverlayRegistryKey';
import {
  lockScroll,
  unlockScroll,
} from '@/app/components/overlays/utils/scrollLock';

export interface OverlayStackProps extends OverlayRegistrationProps {
  lockScroll?: boolean;
}

const stack: OverlayStackProps[] = [];
let listeners: (() => void)[] = [];

export const overlayStack = {
  /**
   * Add an overlay to the stack.
   * @param entry The overlay entry to add (with optional scroll lock)
   */
  open(entry: OverlayStackProps): void {
    stack.push(entry);

    const isFirstWithScrollLock =
      entry.lockScroll && stack.filter((e) => e.lockScroll).length === 1;

    if (isFirstWithScrollLock) {
      lockScroll();
    }

    this.notify();
  },

  /**
   * Remove an overlay from the stack.
   */
  close(props: OverlayRegistrationProps): void {
    const key = getOverlayRegistryKey(props);
    const index = stack.findIndex(
      (entry) => getOverlayRegistryKey(entry) === key
    );
    const [removed] = index !== -1 ? stack.splice(index, 1) : [];

    const shouldUnlockScroll =
      removed?.lockScroll && !stack.some((e) => e.lockScroll);

    if (shouldUnlockScroll) {
      unlockScroll();
    }

    this.notify();
  },

  /**
   * Check if the given overlay is on top of the stack.
   */

  isTop(key: string): boolean {
    const top = stack.at(-1);
    if (!top) return false;

    return getOverlayRegistryKey(top) === key;
  },

  /**
   * Check if the given overlay exists anywhere in the stack.
   */
  has(key: string): boolean {
    return stack.some((entry) => getOverlayRegistryKey(entry) === key);
  },

  /**
   * Subscribe to stack updates (for useSyncExternalStore).
   */
  subscribe(listener: () => void): () => void {
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  },

  /**
   * Notify all listeners about a stack change.
   */
  notify(): void {
    listeners.forEach((l) => l());
  },
};
